import { FC, SyntheticEvent, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import { Box, Tooltip } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import { useSearchParams } from "react-router-dom";

import { measureTypeName } from "../../constants";
import { ANALYTICS_VIEW, ANALYTICS_MEASURE_TYPE } from "../../interfaces";

import { StyledTab } from "../../../../../../../../components/common/styled/tab";

export const AnalyticsViewSwitcher: FC = () => {
  const { formatMessage } = useIntl();
  const measureType = useWatch({ name: measureTypeName });
  const [searchParams, setSearchParams] = useSearchParams();

  const initialView = searchParams.get("view")
    ? (Number(searchParams.get("view")) as ANALYTICS_VIEW)
    : ANALYTICS_VIEW.CHART;

  const [view, setView] = useState<ANALYTICS_VIEW>(initialView);

  const updateSearchParams = (newView: ANALYTICS_VIEW) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (newView === ANALYTICS_VIEW.CHART) {
      nextSearchParams.delete("view");
    } else {
      nextSearchParams.set("view", newView.toString());
    }

    setSearchParams(nextSearchParams);
  };

  const handleViewChange = (_event: SyntheticEvent, newView: string) => {
    const numericView = Number(newView) as ANALYTICS_VIEW;
    if (numericView !== null && measureType === ANALYTICS_MEASURE_TYPE.TREND) {
      setView(numericView);
      updateSearchParams(numericView);
    }
  };

  useEffect(() => {
    const hasViewInQuery = searchParams.get("view") !== null;

    if (measureType !== ANALYTICS_MEASURE_TYPE.TREND && (view !== ANALYTICS_VIEW.CHART || hasViewInQuery)) {
      if (view !== ANALYTICS_VIEW.CHART) {
        setView(ANALYTICS_VIEW.CHART);
      }
      updateSearchParams(ANALYTICS_VIEW.CHART);
    }
  }, [measureType, view, searchParams]);

  return (
    <TabContext value={view.toString()}>
      <TabList
        onChange={handleViewChange}
        aria-label="view switcher"
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{ minHeight: 30, mt: "4px" }}
      >
        <StyledTab
          value={ANALYTICS_VIEW.CHART.toString()}
          sx={{ minHeight: 30 }}
          label={
            <Tooltip title={formatMessage({ id: "enums.analyticsView.CHART" })} arrow>
              <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                {formatMessage({ id: "enums.analyticsView.CHART" })}
              </Box>
            </Tooltip>
          }
        />
        <StyledTab
          value={ANALYTICS_VIEW.TABLE.toString()}
          sx={{ minHeight: 30 }}
          disabled={measureType !== ANALYTICS_MEASURE_TYPE.TREND}
          label={
            <Tooltip title={formatMessage({ id: "enums.analyticsView.TABLE" })} arrow>
              <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                {formatMessage({ id: "enums.analyticsView.TABLE" })}
              </Box>
            </Tooltip>
          }
        />
      </TabList>
    </TabContext>
  );
};
